import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { Calendar, Coins, ShieldCheck, UserCircle2, X } from 'lucide-react';

/**
 * GrantCard — STORY-1212 (B-13) Partner Detail "Meghatalmazás" tab-on
 * megjelenő grant-card (Sally 03-partner §2.2 + §3.5, ADR-124 D-1+D-2).
 *
 * Layout: 2 soros, mobile-friendly (Q2 gate-7 decision 2026-04-24):
 *  - Sor 1: type badge (PERMANENT zöld "Aktív" / ONE_TIME kék "Bérlésig") +
 *    grantee name + revoke CTA (jobbra)
 *  - Sor 2: metadata chip-sor (valid-from / valid-until / value-limit)
 *
 * Revoke button:
 *  - Csak ACTIVE státuszú grantra jelenik meg
 *  - Csak ha `canRevoke=true` (caller dönti el — általában: user a grantor
 *    tenant-je)
 *  - Callback-en keresztül (parent kezeli a modal/confirm flow-t)
 *
 * Az EXPIRED/REVOKED/SUPERSEDED státuszú grantok is megjelennek, de fakultak
 * (opacity-60) + revoke CTA elrejtve. Az "Archívum" szekcióban renderelődnek.
 */

export type GrantCardStatus = 'ACTIVE' | 'REVOKED' | 'EXPIRED' | 'SUPERSEDED';
export type GrantCardType = 'PERMANENT' | 'ONE_TIME';

export interface GrantCardGrant {
  id: string;
  type: GrantCardType;
  status: GrantCardStatus;
  /**
   * Megjelenítendő név (parent dönti el: PERMANENT-nél a granteePartnerId
   * partner.name; ONE_TIME-nál a granteeData.name).
   */
  granteeDisplayName: string;
  validFrom: string; // ISO
  validUntil?: string | null;
  valueLimitHuf?: number | null;
  /** ONE_TIME-nál az adott bérlés száma/azonosítója (opcionális). */
  rentalReference?: string | null;
  /** Revoked grantnél revocationReason megjelenítés. */
  revocationReason?: string | null;
}

export interface GrantCardProps {
  grant: GrantCardGrant;
  /** Revoke callback (csak ACTIVE + canRevoke=true esetén hívódik). */
  onRevoke?: (grantId: string) => void;
  /**
   * Csak akkor engedélyezi a revoke CTA-t, ha a nézőhallgatóság (current user)
   * a grant grantora.
   */
  canRevoke?: boolean;
  /** Loading state a revoke button-ön — parent kezeli ha async folyamatban. */
  isRevoking?: boolean;
  /** Ha true, a card halványabb (opacity-60) — archive view rendelkezésre. */
  archived?: boolean;
  'data-testid'?: string;
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatHuf(value: number): string {
  try {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value} Ft`;
  }
}

export function GrantCard({
  grant,
  onRevoke,
  canRevoke = false,
  isRevoking = false,
  archived = false,
  'data-testid': testId,
}: GrantCardProps) {
  const { t } = useTranslation();
  // @kgc/i18n's context value doesn't expose `i18n.language` — keep date
  // formatting simple with the Hungarian locale (matches rPI ERP default;
  // multi-locale follow-up: wire through a locale selector context).
  const locale = 'hu-HU';
  const showRevoke = grant.status === 'ACTIVE' && canRevoke && onRevoke !== undefined;
  const isTerminal = grant.status !== 'ACTIVE';

  const badgeText =
    grant.type === 'PERMANENT'
      ? t('authGrant.badge.permanentActive')
      : t('authGrant.badge.oneTimeUntilRental');
  const badgeColor =
    grant.type === 'PERMANENT'
      ? 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800'
      : 'bg-sky-100 text-sky-900 border-sky-300 dark:bg-sky-900/30 dark:text-sky-200 dark:border-sky-800';

  return (
    <div
      className={`rounded-md border bg-card p-3 transition-colors ${
        archived || isTerminal ? 'opacity-60' : 'hover:bg-muted/40'
      } ${isTerminal ? 'border-dashed' : ''}`}
      data-testid={testId}
    >
      {/* Row 1: badge + name + revoke CTA */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {/* B-13 Acceptance P2 AC-4 — badge text matches AC literal:
              PERMANENT green "Aktív" / ONE_TIME blue "Bérlésig". Type prefix
              dropped — type is communicated by color. */}
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${badgeColor}`}
            data-testid={`grant-card-badge-${grant.type.toLowerCase()}`}
            title={
              grant.type === 'PERMANENT'
                ? t('authGrant.type.permanent')
                : t('authGrant.type.oneTime')
            }
          >
            <ShieldCheck className="h-3 w-3" aria-hidden="true" />
            <span>{badgeText}</span>
          </span>
          <div className="flex min-w-0 items-center gap-1 text-sm font-medium text-foreground">
            <UserCircle2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="truncate" title={grant.granteeDisplayName}>
              {grant.granteeDisplayName}
            </span>
          </div>
        </div>
        {showRevoke && (
          <Button
            variant="outline"
            size="sm"
            disabled={isRevoking}
            onClick={() => onRevoke!(grant.id)}
            data-testid={`grant-card-revoke-${grant.id}`}
            className="shrink-0 border-red-300 text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950"
          >
            <X className="mr-1 h-3 w-3" aria-hidden="true" />
            {t('authGrant.card.revoke')}
          </Button>
        )}
      </div>

      {/* Row 2: metadata */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1" data-testid="grant-card-valid-from">
          <Calendar className="h-3 w-3" aria-hidden="true" />
          {t('authGrant.card.validFrom')}: {formatDate(grant.validFrom, locale)}
        </span>
        {grant.validUntil && (
          <span className="inline-flex items-center gap-1" data-testid="grant-card-valid-until">
            <Calendar className="h-3 w-3" aria-hidden="true" />
            {t('authGrant.card.validUntil')}: {formatDate(grant.validUntil, locale)}
          </span>
        )}
        {grant.valueLimitHuf !== null && grant.valueLimitHuf !== undefined && (
          <span className="inline-flex items-center gap-1" data-testid="grant-card-value-limit">
            <Coins className="h-3 w-3" aria-hidden="true" />
            {t('authGrant.card.valueLimit')}: {formatHuf(grant.valueLimitHuf)}
          </span>
        )}
        {grant.rentalReference && grant.type === 'ONE_TIME' && (
          <span className="inline-flex items-center gap-1" data-testid="grant-card-rental-ref">
            {t('authGrant.card.rental')}: {grant.rentalReference}
          </span>
        )}
        {isTerminal && grant.revocationReason && (
          <span
            className="inline-flex items-center gap-1 text-red-700 dark:text-red-300"
            data-testid="grant-card-revocation-reason"
          >
            {t('authGrant.card.revokedReason')}: {grant.revocationReason}
          </span>
        )}
      </div>
    </div>
  );
}
